import { TranslocoService } from '@jsverse/transloco';

import { DeliveryPoint } from '@models/account/delivery-point.model';
import { Country } from '@models/types/country';

type KeyValue = Record<string, string>;

export class Utils {
  static sumArray(arr: number[]): number {
    return arr.reduce((sum, currentValue) => sum + currentValue, 0);
  }

  /**
   * Utility function to create a K:V from a list of strings
   */
  static strEnum<T extends string>(o: T[]): { [K in T]: K } {
    return o.reduce((res, key) => {
      res[key] = key;

      return res;
    }, Object.create(null));
  }

  static formatBytes(bytes: number, decimals = 0): string {
    if (bytes === 0) {
      return '0 B';
    }

    const k = 1024;
    const dm = decimals || 2;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  static getExtension(filename: string): string | null {
    const regex = /(?:\.([^.]+))?$/;
    const result = regex.exec(filename);
    const extension = result ? result[1] : null;

    return extension ? extension.toLowerCase() : extension;
  }

  static getBasename(filename: string): string {
    const extension = this.getExtension(filename);

    if (extension) {
      return filename.slice(0, -1 * (extension.length + 1));
    }

    return filename;
  }

  static isJson(data: string): boolean {
    let valid = true;

    try {
      JSON.parse(data);
    } catch {
      valid = false;
    }

    return valid;
  }

  static isXml(data: string): boolean {
    return data.indexOf('<?xml') === 0;
  }

  /**
   * Round number to precision
   */
  static precisionRound(number: number, precision: number) {
    const factor = Math.pow(10, precision);

    return Math.round(number * factor) / factor;
  }

  static isNotNullAndNotUndefined(value: any): boolean {
    return !Utils.isNullOrUndefined(value);
  }

  static unique<T>(items: T[], getIdFromItem?: (item: T) => unknown): T[] {
    const defaultGetIdFromItem = (item: T) => {
      if (typeof item === 'object') {
        throw new Error('You need to set a getIdFromItem() as 2nd parameter of unique() for an array of objects');
      }
      return item;
    };
    const idFromItem = getIdFromItem || defaultGetIdFromItem;
    const cache = new Map<any, boolean>();

    return items.filter((item: T) => {
      const id = idFromItem(item);
      if (!cache.has(id)) {
        cache.set(id, true);
        return true;
      }
      return false;
    });
  }

  static capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  static getBrowser(withAgent: boolean): string {
    let browser = '';
    const agent = window.navigator.userAgent;
    if (agent.indexOf('Firefox') >= 0) {
      browser = 'Firefox';
    } else if (agent.indexOf('Trident') >= 0) {
      browser = 'Internet Explorer';
    } else if (agent.indexOf('Edge') >= 0) {
      browser = 'Edge';
    } else if (agent.indexOf('Chrome') >= 0) {
      browser = 'Chrome';
    } else if (agent.indexOf('Safari') >= 0) {
      browser = 'Safari';
    }
    if (withAgent) {
      return `${browser} (${agent})`;
    }
    return browser;
  }

  static isNullOrUndefined(value: any) {
    return value === null || value === undefined;
  }

  static mapper(rawData: KeyValue, mapping: KeyValue) {
    const init: KeyValue = {};

    return Object.keys(mapping).reduce((acc, key) => {
      acc[mapping[key]] = rawData[key];
      return acc;
    }, init);
  }

  static camelToSnakeCase(camelInputStr: string): string {
    return camelInputStr.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  }

  static ignoreAccent(value: string): string {
    return value.normalize('NFD').replace(/\p{Diacritic}/gu, '');
  }

  static deepCompare(obj1, obj2): boolean {
    // Vérifier si les deux objets sont du même type
    if (typeof obj1 !== typeof obj2) {
      return false;
    }

    // Si les objets sont des valeurs primitives (string, number, boolean, etc.)
    if (typeof obj1 !== 'object' || obj1 === null || obj2 === null) {
      return obj1 === obj2;
    }

    // Vérifier si les deux objets ont le même nombre de propriétés
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    // Vérifier si les propriétés de chaque objet sont équivalentes
    for (const key of keys1) {
      if (!this.deepCompare(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  }

  static genericComparer(a, b, direction = 'asc') {
    const compare = direction === 'desc' ? -1 : 1;

    if (typeof a === 'number' && typeof b === 'number') {
      return compare * (a - b); // Numeric sorting for numbers
    } else if (typeof a === 'string' && typeof b === 'string') {
      return compare * a.localeCompare(b); // Alphabetic sorting for strings
    } else {
      // By default, sort in alphabetic order based on the string representation
      return compare * String(a).localeCompare(String(b));
    }
  }

  static parseHeaderFileName(contentDisposition: string) {
    /***
     example : attachment; filename=export-power.xlsx
     */
    const lookup = 'filename=';
    return contentDisposition
      .slice(contentDisposition.indexOf(lookup) + lookup.length)
      .replace('"', '')
      .replace('"', '');
  }

  static removeDuplicates(array, prop) {
    const uniqueValues = new Set();
    return array.filter((obj) => !uniqueValues.has(obj[prop]) && uniqueValues.add(obj[prop]));
  }

  static getCurrencySymbol(currency: string) {
    return Intl.NumberFormat('en', { style: 'currency', currency })
      .formatToParts()
      .find((part) => part.type === 'currency').value;
  }

  static translateCountry = (country: string | Country, translateService: TranslocoService): string => {
    return translateService.translate<string>('countries.' + country);
  };

  static sortCountriesWithTranslation(a: string, b: string, translateService: TranslocoService) {
    return Utils.translateCountry(a, translateService).localeCompare(Utils.translateCountry(b, translateService));
  }

  static prepareDeliveryPoints(deliveryPoints: DeliveryPoint[]) {
    return deliveryPoints.map((dp) => ({ gridOperatorCode: dp.gridOperator, mpCode: dp.meteringPoint }));
  }

  static monthToMilliseconds(month: number): number {
    return month * 31 * 24 * 3600 * 1000;
  }

  static normalizeText(txt: string, removeSpaces = false, removeDashes = false, removeAmpersands = false): string {
    const accent = [
      /[\xc0-\xc6]/g,
      /[\xe0-\xe6]/g, // A, a
      /[\xc8-\xcb]/g,
      /[\xe8-\xeb]/g, // E, e
      /[\xcc-\xcf]/g,
      /[\xec-\xef]/g, // I, i
      /[\xd2-\xd8]/g,
      /[\xf2-\xf8]/g, // O, o
      /[\xd9-\xdc]/g,
      /[\xf9-\xfc]/g, // U, u
      /[\xd1]/g,
      /[\xf1]/g, // N, n
      /[\xc7]/g,
      /[\xe7]/g, // C, c
    ];
    const noaccent = ['A', 'a', 'E', 'e', 'I', 'i', 'O', 'o', 'U', 'u', 'N', 'n', 'C', 'c'];
    for (let i = 0; i < accent.length; i++) {
      txt = txt.replace(accent[i], noaccent[i]);
    }
    if (removeSpaces) {
      txt = txt.replace(/\s+/g, '');
    }
    if (removeDashes) {
      txt = txt.replace(/-/g, '');
    }
    if (removeAmpersands) {
      txt = txt.replace(/&/g, '');
    }
    return txt.toLowerCase().trim();
  }

  static complexFilter(items: any[], fields: string[], value?: any): any[] {
    if (!items) {
      return [];
    }
    const val = value ? ('' + value).trim() : value;
    if (val) {
      const pattern = val.replace(/[?=]/g, '\\?').replace(/[!=]/g, '\\!').replace(/[.=]/g, '\\.').replace(/[*]/g, '\\*');
      const regX = new RegExp('(?=.*' + pattern.trim().split(' ').join(')(?=.*') + ').*', 'gi');
      const results = items.filter((it) => {
        const keys = Object.keys(it).filter((k) => fields.includes(k));
        for (const k of keys) {
          if (it[k].match(regX)) {
            return true;
          }
        }
      });
      return results;
    }
    return items;
  }
}
